import React from 'react';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Type, Heading1, Heading2, AlignLeft, X } from 'lucide-react';

const textStyles = [
  {
    id: 'heading',
    name: 'Add a heading',
    preview: 'Add a heading',
    fontSize: '48px',
    fontWeight: 'bold',
    fontFamily: 'Poppins'
  },
  {
    id: 'subheading',
    name: 'Add a subheading',
    preview: 'Add a subheading',
    fontSize: '32px',
    fontWeight: '600',
    fontFamily: 'Poppins'
  },
  {
    id: 'body',
    name: 'Add body text',
    preview: 'Add body text',
    fontSize: '18px',
    fontWeight: 'normal',
    fontFamily: 'Poppins'
  },
  {
    id: 'small',
    name: 'Add small text',
    preview: 'Add small text',
    fontSize: '14px',
    fontWeight: 'normal',
    fontFamily: 'Poppins'
  }
];

const fontCombinations = [
  {
    id: 'modern',
    name: 'Modern',
    heading: { fontFamily: 'Montserrat', fontWeight: 'bold' },
    body: { fontFamily: 'Open Sans', fontWeight: 'normal' }
  },
  {
    id: 'elegant',
    name: 'Elegant',
    heading: { fontFamily: 'Playfair Display', fontWeight: 'bold' },
    body: { fontFamily: 'Lato', fontWeight: 'normal' }
  },
  {
    id: 'minimal',
    name: 'Minimal',
    heading: { fontFamily: 'Inter', fontWeight: '600' },
    body: { fontFamily: 'Inter', fontWeight: 'normal' }
  },
  {
    id: 'bold',
    name: 'Bold',
    heading: { fontFamily: 'Oswald', fontWeight: 'bold' },
    body: { fontFamily: 'Roboto', fontWeight: 'normal' }
  }
];

const TextPanel = ({ onAddElement, onClose, selectedElement, onEnterTextEdit }) => {
  const handleAddText = (style) => {
    // If a text element is already selected, enter its edit mode instead of inserting a duplicate
    if (selectedElement?.type === 'text' && onEnterTextEdit) {
      onEnterTextEdit(selectedElement.id);
      return;
    }

    const widthMap = {
      heading: 400,
      subheading: 300,
      body: 250,
      small: 200
    };

    const heightMap = {
      heading: 70,
      subheading: 50,
      body: 40,
      small: 30
    };

    onAddElement({
      type: 'text',
      text: style.preview,
      x: 100,
      y: 100,
      width: widthMap[style.id] || 200,
      height: heightMap[style.id] || 50,
      fontSize: style.fontSize,
      fontFamily: style.fontFamily,
      color: '#000000',
      fontWeight: style.fontWeight,
      textAlign: 'left'
    });
  };

  return (
    <div className="h-full flex flex-col">
      <div className="p-4 border-b">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900">Text</h3>
            <p className="text-xs text-gray-500 mt-1">Click to add text to your design</p>
          </div>
          {onClose && (
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-6">
          {/* Quick Add Buttons */}
          <div className="space-y-2">
            <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Quick Add</p>
            {textStyles.map((style) => (
              <button
                key={style.id}
                onClick={() => handleAddText(style)}
                className="w-full text-left px-4 py-3 bg-gray-50 hover:bg-purple-50 border-2 border-transparent hover:border-purple-300 rounded-xl transition-all group"
                data-testid={`text-style-${style.id}`}
              >
                <span 
                  style={{ 
                    fontSize: style.id === 'heading' ? '20px' : style.id === 'subheading' ? '16px' : '14px',
                    fontWeight: style.fontWeight,
                    fontFamily: style.fontFamily
                  }}
                  className="text-gray-800 group-hover:text-purple-700"
                >
                  {style.name}
                </span>
              </button>
            ))}
          </div>

          {/* Font Combinations */}
          <div className="space-y-3">
            <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Font Combinations</p>
            <div className="grid grid-cols-2 gap-2">
              {fontCombinations.map((combo) => (
                <button
                  key={combo.id}
                  onClick={() => handleAddText({
                    ...textStyles[0],
                    fontFamily: combo.heading.fontFamily,
                    fontWeight: combo.heading.fontWeight
                  })}
                  className="p-3 bg-gray-50 hover:bg-purple-50 border-2 border-transparent hover:border-purple-300 rounded-xl transition-all text-left"
                  data-testid={`font-combo-${combo.id}`}
                >
                  <p 
                    className="text-sm font-bold truncate"
                    style={{ fontFamily: combo.heading.fontFamily }}
                  >
                    {combo.name}
                  </p>
                  <p 
                    className="text-xs text-gray-500 truncate"
                    style={{ fontFamily: combo.body.fontFamily }}
                  >
                    Body text
                  </p>
                </button>
              ))}
            </div>
          </div>

          {/* Text Effects - Coming Soon */}
          <div className="space-y-2">
            <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Text Effects</p>
            <div className="bg-gray-100 rounded-xl p-4 text-center">
              <p className="text-sm text-gray-500">Curved text, shadows & more</p>
              <p className="text-xs text-purple-600 font-medium mt-1">Coming Soon</p>
            </div>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
};

export default TextPanel;
